import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

interface CustomCaptchaProps {
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  value: string;
  error?: boolean;
  helperText?: string;
  onCaptchaGenerated: (captcha: string) => void;
  captchaLabel?: string;
}

const generateCaptchaText = () => {
  let captcha = "";
  for (let i = 0; i < 2; i++) {
    captcha += String.fromCharCode(Math.floor(Math.random() * 26) + 65);
    captcha += String.fromCharCode(Math.floor(Math.random() * 26) + 97);
    captcha += String.fromCharCode(Math.floor(Math.random() * 10) + 48);
  }
  return captcha.split("").sort(() => Math.random() - 0.5).join("");
};

const drawCaptcha = (ctx: CanvasRenderingContext2D, captcha: string) => {
  ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  const letterSpace = 150 / captcha.length;
  for (let i = 0; i < captcha.length; i++) {
    ctx.font = "18px 'Courier New', monospace";
    ctx.fillStyle = i % 2 === 0 ? "rgba(29,47,65,0.75)" : "rgba(29,47,65,0.45)";
    ctx.fillText(captcha[i], 20 + i * letterSpace, Math.floor(Math.random() * 14) + 26, 100);
  }
};

const CustomCaptcha: React.FC<CustomCaptchaProps> = ({ onChange, value, error, helperText, onCaptchaGenerated, captchaLabel = 'Enter the characters above' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [, forceUpdate] = useState(0);

  const refresh = () => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const text = generateCaptchaText();
    drawCaptcha(ctx, text);
    onCaptchaGenerated(text);
    forceUpdate(n => n + 1);
  };

  useEffect(() => {
    refresh();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <canvas
          ref={canvasRef}
          width="200"
          height="52"
          style={{ border: "1px solid rgba(29,47,65,0.2)", display: "block" }}
        />
        <IconButton onClick={refresh} size="small" sx={{ color: "rgba(29,47,65,0.4)", "&:hover": { color: "#1D2F41" } }}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>
      <TextField
        fullWidth
        name="captcha"
        label={captchaLabel}
        value={value}
        onChange={onChange}
        error={error}
        helperText={helperText}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
            fontFamily: '"Calibri", sans-serif',
            fontSize: "0.95rem",
            "& fieldset": { borderColor: "rgba(29,47,65,0.2)" },
            "&:hover fieldset": { borderColor: "rgba(29,47,65,0.4)" },
            "&.Mui-focused fieldset": { borderColor: "#1D2F41", borderWidth: "1px" },
          },
          "& .MuiInputLabel-root": {
            fontFamily: '"Calibri", sans-serif',
            fontSize: "0.95rem",
            color: "rgba(29,47,65,0.5)",
            "&.Mui-focused": { color: "#1D2F41" },
          },
        }}
      />
    </Box>
  );
};

export default CustomCaptcha;
